import { z } from 'zod'

import type { SettingsIn } from '@/api/endpoints/settings'

/**
 * Pure pieces of the Configurações forms — schema, which fields belong to
 * which tab, and the GET ↔ form ↔ PUT mapping — kept out of the component
 * so they're testable in the node test environment.
 */

// Empty string = "no override, fall back to the default". Numeric fields
// use NaN sentinel via z.coerce + nullable().
export const schema = z.object({
  llm_model: z.string(),
  llm_provider: z.string(),
  llm_api_key: z.string(),
  ollama_desktop_url: z.string(),
  default_language: z.string(),
  subtitle_style: z.string(),
  default_tts_voice_pt: z.string(),
  default_tts_voice_en: z.string(),
  thumbnail_style: z.string(),
  discord_cost_alert_usd: z.union([z.coerce.number().min(0), z.literal('')]),
  discord_pipeline_duration_warn_s: z.union([z.coerce.number().int().min(0), z.literal('')]),
})

export type FormValues = z.infer<typeof schema>

/** The tabs of Configurações that this form backs — each saves only its own fields. */
export type GlobalDefaultsSection = 'generation' | 'videos' | 'notifications'

export const SECTION_FIELDS: Record<GlobalDefaultsSection, ReadonlyArray<keyof FormValues>> = {
  generation: ['llm_provider', 'llm_model', 'llm_api_key', 'default_language', 'ollama_desktop_url'],
  videos: ['subtitle_style', 'thumbnail_style', 'default_tts_voice_pt', 'default_tts_voice_en'],
  notifications: ['discord_cost_alert_usd', 'discord_pipeline_duration_warn_s'],
}

export const EMPTY_VALUES: FormValues = {
  llm_model: '',
  llm_provider: '',
  llm_api_key: '',
  ollama_desktop_url: '',
  default_language: '',
  subtitle_style: '',
  default_tts_voice_pt: '',
  default_tts_voice_en: '',
  thumbnail_style: '',
  discord_cost_alert_usd: '',
  discord_pipeline_duration_warn_s: '',
}

export function toFormValues(out: Partial<Record<keyof FormValues, unknown>> | undefined): FormValues {
  if (!out) return EMPTY_VALUES
  return {
    llm_model: (out.llm_model as string | null) ?? '',
    llm_provider: (out.llm_provider as string | null) ?? '',
    // Sempre vazio: a chave é write-only e o GET nunca a devolve. O que a tela
    // mostra sobre ela vem de ``credentials`` (configurado? últimos 4?).
    llm_api_key: '',
    ollama_desktop_url: (out.ollama_desktop_url as string | null) ?? '',
    default_language: (out.default_language as string | null) ?? '',
    subtitle_style: (out.subtitle_style as string | null) ?? '',
    default_tts_voice_pt: (out.default_tts_voice_pt as string | null) ?? '',
    default_tts_voice_en: (out.default_tts_voice_en as string | null) ?? '',
    thumbnail_style: (out.thumbnail_style as string | null) ?? '',
    discord_cost_alert_usd: (out.discord_cost_alert_usd as number | null) ?? '',
    discord_pipeline_duration_warn_s:
      (out.discord_pipeline_duration_warn_s as number | null) ?? '',
  }
}

export function toPayload(values: FormValues, section: GlobalDefaultsSection): SettingsIn {
  // ``''`` (the "use default" sentinel) maps to ``null`` on the wire so the
  // backend clears the override. Numeric fields are coerced by zod already.
  const norm = <T,>(v: T | '' | null): T | null => (v === '' || v == null ? null : v)
  // Indexed access on optional props (``SettingsIn['x']``) widens to include
  // ``undefined``; with ``exactOptionalPropertyTypes`` the assignment then
  // rejects it. ``norm`` only returns ``T | null`` so excluding undefined
  // from the cast restores the precise narrow union type.
  const full: SettingsIn = {
    llm_model: norm(values.llm_model),
    llm_provider: norm(values.llm_provider) as Exclude<SettingsIn['llm_provider'], undefined>,
    // Campo em branco = "não mexer na chave". Só vai para o wire quando o
    // usuário digitou algo, senão salvar qualquer outra config apagaria a
    // chave existente.
    ...(values.llm_api_key ? { llm_api_key: values.llm_api_key } : {}),
    ollama_desktop_url: norm(values.ollama_desktop_url),
    default_language: norm(values.default_language) as Exclude<SettingsIn['default_language'], undefined>,
    subtitle_style: norm(values.subtitle_style) as Exclude<SettingsIn['subtitle_style'], undefined>,
    default_tts_voice_pt: norm(values.default_tts_voice_pt),
    default_tts_voice_en: norm(values.default_tts_voice_en),
    thumbnail_style: norm(values.thumbnail_style) as Exclude<SettingsIn['thumbnail_style'], undefined>,
    discord_cost_alert_usd: norm(values.discord_cost_alert_usd),
    discord_pipeline_duration_warn_s: norm(values.discord_pipeline_duration_warn_s),
  }
  // Only the section's own fields go out: the PUT is a partial update, so the
  // other tabs keep what they have instead of being re-sent from this form.
  const out: Record<string, unknown> = {}
  for (const key of SECTION_FIELDS[section]) {
    if (key in full) out[key] = full[key]
  }
  return out
}
